// SearchModules.js

// Chakra imports
import {
  Box,
  Flex,
  Grid,
  Button,
  Text,
  useColorModeValue,
  useToast,
  Input,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";

// React & libs
import React, { useState, useCallback, useRef, useEffect } from "react";
import Card from "components/Card/Card.js";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner.js";
import DataTable, { createTheme } from "react-data-table-component";
import { useCookies } from "react-cookie";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import TemplateOtherProgram from "./TemplateOtherProgram.xlsx";
import TemplateInvoices from "./TemplateInvoices.xlsx";
import {
  pdf,
  Page,
  Text as Text1,
  Document,
  StyleSheet,
  View,
  Font,
} from "@react-pdf/renderer";
import Header from "./components/Header";
import Policy from "policyDouble.pdf";
import Manual from "manualDouble.pdf";

// Theme for DataTable
createTheme("dark", {
  background: "transparent",
});

// Register font (όπως στο Sampling)
Font.register({
  family: "OpenSans",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/opensans/v20/mem5YaGs126MiZpBA-UN_r8-VeJoCqeDjg.ttf",
    },
    {
      src: "https://fonts.gstatic.com/s/opensans/v20/mem5YaGs126MiZpBA-UN7rg-VeJoCqeDjg.ttf",
      fontStyle: "bold",
    },
  ],
});

// PDF styles (κοινά για όλα τα templates – αλλάζουν μόνο οι στήλες)
const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 20,
  },
  container: {
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "stretch",
    flexDirection: "column",
  },
  infoText: {
    fontFamily: "OpenSans",
    fontSize: 8,
    marginVertical: 4,
  },
  sectionTitle: {
    fontFamily: "OpenSans",
    fontSize: 9,
    marginTop: 10,
    marginBottom: 6,
    fontStyle: "bold",
  },
  tableStyle: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 0,
    marginTop: 4,
  },
  tableHeaderRow: {
    width: "100%",
    flexDirection: "row",
  },
  tableRow: {
    width: "100%",
    flexDirection: "row",
  },
  tableHeaderCol: {
    flex: 1,
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCol: {
    flex: 1,
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    fontFamily: "OpenSans",
    fontSize: 8,
    paddingVertical: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
  },
  tableHeader: {
    fontFamily: "OpenSans",
    fontStyle: "bold",
    fontSize: 8,
    textAlign: "center",
    justifyContent: "center",
    paddingVertical: 4,
    paddingHorizontal: 2,
    // ΧΩΡΙΣ minHeight εδώ — το ύψος θα είναι όσο χρειάζεται για το κείμενο
  },
  footer: {
    width: "100%",
    fontSize: 6,
    fontFamily: "OpenSans",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: "gray",
    borderTopStyle: "dashed",
    marginTop: 10,
  },
});


// Helper: έλεγχος αν JSON έχει εγγραφές σε afm ή voucher
const hasAfmOrVoucherData = (obj) => {
  if (!obj || typeof obj !== "object") return false;

  if (Array.isArray(obj)) {
    return obj.some((item) => hasAfmOrVoucherData(item));
  }

  if (Object.prototype.hasOwnProperty.call(obj, "afm")) {
    const val = obj.afm;
    if (Array.isArray(val)) {
      if (val.length > 0) return true;
    } else if (val !== null && val !== undefined && String(val).trim() !== "") {
      return true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(obj, "voucher")) {
    const val = obj.voucher;
    if (Array.isArray(val)) {
      if (val.length > 0) return true;
    } else if (val !== null && val !== undefined && String(val).trim() !== "") {
      return true;
    }
  }

  return Object.values(obj).some((value) => hasAfmOrVoucherData(value));
};

// Helper: εξαγωγή flat array records από οποιοδήποτε JSON
const extractRecordsFromData = (data) => {
  if (!data) return [];

  if (Array.isArray(data)) return data;

  // Αν υπάρχει data.result, δουλεύουμε πάνω σε αυτό
  const root =
    data.result && typeof data.result === "object" ? data.result : data;

  let records = [];
  if (Array.isArray(root.afm)) records = records.concat(root.afm);
  if (Array.isArray(root.voucher)) records = records.concat(root.voucher);

  if (records.length === 0) {
    const firstArray = Object.values(root).find((v) => Array.isArray(v));
    if (firstArray) records = firstArray;
  }

  if (records.length === 0) {
    records = [root];
  }

  return records;
};


// Helper: 2D array για αποστολή ως data (όλα string)
const to2DArrayFromRows = (rows) => {
  if (!rows || rows.length === 0) return [];
  const headers = Object.keys(rows[0]).map((h) => String(h));
  const matrix = [headers];
  rows.forEach((r) => {
    const rowArr = headers.map((h) => {
      const val = r[h];
      return val === null || val === undefined ? "" : String(val);
    });
    matrix.push(rowArr);
  });
  return matrix;
};

// Helper: format date/time (για header pdf και προβολή)
const formatDateTimeForHeader = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

// Helper για ανάγνωση τιμής από πολλά πιθανά keys
const getFieldValue = (record, candidates) => {
  for (const key of candidates) {
    if (record[key] !== undefined && record[key] !== null) {
      return String(record[key]);
    }
  }
  return "";
};

// Helper: μετατρέπει "a,b,c" σε "a\nb\nc"
const splitByCommaToLines = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (!str.includes(",")) return str;
  return str.split(",").join("\n");
};

// Helper: μικρό format dd/mm/yy
const formatDateToDDMMYY = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(2);
  return `${day}/${month}/${year}`;
};

// Helper: normalisation ημερομηνίας τιμολογίου -> dd/mm/yy
const normalizeVoucherDate = (value) => {
  if (value === null || value === undefined || value === "") return "";

  // Excel serial number
  if (typeof value === "number") {
    try {
      if (XLSX.SSF && XLSX.SSF.parse_date_code) {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (parsed) {
          const jsDate = new Date(parsed.y, parsed.m - 1, parsed.d);
          return formatDateToDDMMYY(jsDate);
        }
      }
    } catch (e) {
      // fall-through
    }
  }

  const str = String(value).trim();
  if (!str) return "";

  // Already dd/mm/yy
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(str)) return str;

  // dd/mm/yyyy or dd-mm-yyyy
  let m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    let dd = m[1].padStart(2, "0");
    let mm = m[2].padStart(2, "0");
    let yy = m[3];
    if (yy.length === 4) yy = yy.slice(2);
    return `${dd}/${mm}/${yy}`;
  }

  // yyyy-mm-dd or yyyy/mm/dd
  m = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (m) {
    let dd = m[3].padStart(2, "0");
    let mm = m[2].padStart(2, "0");
    let yy = m[1].slice(2);
    return `${dd}/${mm}/${yy}`;
  }

  // Αν δεν μπορεί να μετατραπεί, το επιστρέφουμε όπως είναι
  return str;
};

const isValidDDMMYY = (value) => {
  if (!value) return false;
  const str = String(value).trim();
  if (!/^\d{2}\/\d{2}\/\d{2}$/.test(str)) return false;
  const [ddStr, mmStr, yyStr] = str.split("/");
  const dd = Number(ddStr);
  const mm = Number(mmStr);
  const yy = Number(yyStr);
  const year = 2000 + yy;
  const date = new Date(year, mm - 1, dd);
  return (
    date.getFullYear() === year &&
    date.getMonth() === mm - 1 &&
    date.getDate() === dd
  );
};

// Καθαρισμός εγγραφής history voucher -> μόνο τα πεδία που μας ενδιαφέρουν
// και datetime από backend (called_at ή date)
const mapVoucherHistoryRecord = (r) => ({
  afm: r.afm,
  voucher: r.voucher ?? r["Αριθμός Τιμολογίου"],
  voucher_date: r.voucher_date ?? r["Ημερομηνία Τιμολογίου"],
  voucher_series: r.voucher_series ?? r["Σειρά Τιμολογίου"],
  voucher_vat: r.voucher_vat ?? r.vat ?? r["Ποσό ΦΠΑ"],
  username: r.username,
  cn_codes: r.cn_codes,
  ada: r.ada,
  datetime: r.called_at || r.date || r["Ημερομηνία κλήσης"],
});

// Helper: ανάγνωση creds από cookies (υποστηρίζει stringified JSON)
const getUserMetaFromCookies = (cookies) => {
  if (!cookies || !cookies.creds) {
    return { username: "", userid: "" };
  }

  let raw = cookies.creds;

  // Αν είναι ήδη object (π.χ. από react-cookie)
  if (typeof raw === "object" && raw !== null) {
    const username = raw.username || raw.mail || raw.email || "";
    const userid = raw.userid || raw.id || "";
    return { username, userid };
  }

  // Αν είναι string (JSON.stringify από Configurator)
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.username || parsed.userid)) {
        return {
          username: parsed.username || parsed.mail || parsed.email || "",
          userid: parsed.userid || parsed.id || "",
        };
      }
    } catch (e) {
      // Αν δεν είναι έγκυρο JSON αλλά υπάρχει μη-κενό string,
      // τουλάχιστον δείχνει ότι κάτι υπάρχει.
      if (raw.trim().length > 0) {
        return { username: raw, userid: "" };
      }
    }
  }

  return { username: "", userid: "" };
};

// =============================
// XLSX Row builders ευθυγραμμισμένα με τα PDFs
// =============================

// Αρχείο ΑΦΜ (ιστορικό & batch) -> ίδιο με AfmFilePdfDocument
/*const buildAfmFileExcelRows = (records) => {
  if (!records || !records.length) return [];
  return records.map((record) => ({
    "Αριθμός Φορολογικού Μητρώου (ΑΦΜ)": getFieldValue(record, [
      "AfmValue",
      "afm",
      "ΑΦΜ",
    ]),
    "Κωδικοί πράξεων": getFieldValue(record, [
      "cn_codes",
      "cnCodes",
      "cn",
      "CN",
    ]),
    "Αριθμοί Διαδικτυακής Ανάρτησης (ΑΔΑ)": getFieldValue(record, [
      "ada",
      "ΑΔΑ",
    ]),
  }));
};*/

const buildAfmFileExcelRows = (records) => {
  if (!records || !records.length) return [];

  const rows = [];

  records.forEach((record) => {
    // Βασικές στήλες ΧΩΡΙΣ συνολικό ΑΔΑ
    const baseRow = {
      "Αριθμός Φορολογικού Μητρώου (ΑΦΜ)": getFieldValue(record, [
        "AfmValue",
        "afm",
        "ΑΦΜ",
      ]),
      "Ημερομηνία & ώρα κλήσης": getFieldValue(record, [
        "datetime",
        "dateTime",
        "called_at",
        "date",
        "Ημερομηνία κλήσης",
      ]),
    };

    const diavgeia = record.diavgeia_decisions;

    // Αν δεν υπάρχει Diavgeia ή είναι κενό → μόνο η βασική γραμμή
    if (!diavgeia || !Array.isArray(diavgeia) || diavgeia.length === 0) {
      rows.push(baseRow);
      return;
    }

    let pushedAtLeastOne = false;

    diavgeia.forEach((termEntry) => {
      const term = termEntry?.term ?? "";
      const decisions = Array.isArray(termEntry?.decisions)
        ? termEntry.decisions
        : [];

      // Αν δεν υπάρχουν αποφάσεις για το συγκεκριμένο term,
      // βάζουμε μία γραμμή μόνο με το term και κενά πεδία Diavgeia
      if (decisions.length === 0) {
        rows.push({
          ...baseRow,
          "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
          "ΑΔΑ": "",
          "Αριθμός Πρωτοκόλλου": "",
          "Ημερομηνία έκδοσης": "",
          "Ημερομηνία ανάρτησης": "",
          "Θέμα": "",
          "Τύπος Απόφασης": "",
          "Φορέας Έκδοσης": "",
          "Σύνδεσμος Απόφασης": "",
        });
      } else {
        decisions.forEach((d) => {
          pushedAtLeastOne = true;
          rows.push({
            ...baseRow,
            "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
            "ΑΔΑ": d?.ada ?? "",
            "Αριθμός Πρωτοκόλλου": d?.protocolNumber ?? "",
            "Ημερομηνία έκδοσης": d?.issueDate ?? "",
            "Ημερομηνία ανάρτησης": d?.submissionTimestamp ?? "",
            "Θέμα": d?.subject ?? "",
            "Τύπος Απόφασης": d?.decisionTypeLabel ?? "",
            "Φορέας Έκδοσης": d?.organizationLabel ?? "",
            "Σύνδεσμος Απόφασης": d?.documentUrl ?? "",
          });
        });
      }
    });

    // Safety net (σπάνια θα χρειαστεί, αλλά για σιγουριά):
    if (
      !pushedAtLeastOne &&
      rows.filter(
        (r) =>
          r["Αριθμός Φορολογικού Μητρώου (ΑΦΜ)"] ===
          baseRow["Αριθμός Φορολογικού Μητρώου (ΑΦΜ)"]
      ).length === 0
    ) {
      rows.push(baseRow);
    }
  });

  return rows;
};


// Αρχείο Τιμολογίων (ιστορικό & όμοιο με VoucherFilePdfDocument)
const buildVoucherHistoryExcelRows = (records) => {
  if (!records || !records.length) return [];

  // Ευθυγράμμιση πεδίων όπως στο mapVoucherHistoryRecord
  const normalized = records.map(mapVoucherHistoryRecord);

  const rows = [];

  normalized.forEach((record) => {
    // Βασικές στήλες ΧΩΡΙΣ συνολικό ΑΔΑ
    const baseRow = {
      "ΑΦΜ": getFieldValue(record, ["afm", "ΑΦΜ", "AfmValue"]),
      "Αριθμός Τιμολογίου": getFieldValue(record, [
        "voucher",
        "invoice_number",
        "InvoiceNumber",
        "Αριθμός Τιμολογίου",
        "Αριθμός Τιμολόγιου",
      ]),
      "Σειρά Τιμολογίου": getFieldValue(record, [
        "voucher_series",
        "Σειρά",
        "Σειρά Τιμολογίου",
      ]),
      "Ημερομηνία Τιμολογίου": getFieldValue(record, [
        "voucher_date",
        "Ημερομηνία Τιμολογίου",
        "Ημερομηνία",
      ]),
      "Ποσό ΦΠΑ": getFieldValue(record, ["voucher_vat", "ΦΠΑ", "Ποσό ΦΠΑ"]),
      "Ημερομηνία & ώρα κλήσης": getFieldValue(record, [
        "datetime",
        "dateTime",
        "called_at",
        "date",
        "Ημερομηνία κλήσης",
      ]),
      "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": getFieldValue(record, ["cn_codes"]),
    };

    let diavgeia = record.diavgeia_decisions;

    // Αν έρχεται από backend ως JSON string, κάνε parse
    if (typeof diavgeia === "string") {
      try {
        diavgeia = JSON.parse(diavgeia);
      } catch (e) {
        diavgeia = null;
      }
    }

    // Αν δεν υπάρχει ή είναι κενό → μόνο η βασική γραμμή
    if (!diavgeia || !Array.isArray(diavgeia) || diavgeia.length === 0) {
      rows.push(baseRow);
      return;
    }

    let pushedAtLeastOne = false;

    diavgeia.forEach((termEntry) => {
      const term = termEntry?.term ?? "";
      const decisions = Array.isArray(termEntry?.decisions)
        ? termEntry.decisions
        : [];

      // Αν δεν υπάρχουν αποφάσεις για το συγκεκριμένο term,
      // βάζουμε μία γραμμή μόνο με το term και κενά πεδία Diavgeia
      if (decisions.length === 0) {
        rows.push({
          ...baseRow,
          "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
          "ΑΔΑ": "",
          "Αριθμός Πρωτοκόλλου": "",
          "Ημερομηνία έκδοσης": "",
          "Ημερομηνία ανάρτησης": "",
          "Θέμα": "",
          "Τύπος Απόφασης": "",
          "Φορέας Έκδοσης": "",
          "Σύνδεσμος Απόφασης": "",
        });
      } else {
        decisions.forEach((d) => {
          pushedAtLeastOne = true;
          rows.push({
            ...baseRow,
            "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
            "ΑΔΑ": d?.ada ?? "",
            "Αριθμός Πρωτοκόλλου": d?.protocolNumber ?? "",
            "Ημερομηνία έκδοσης": d?.issueDate ?? "",
            "Ημερομηνία ανάρτησης": d?.submissionTimestamp ?? "",
            "Θέμα": d?.subject ?? "",
            "Τύπος Απόφασης": d?.decisionTypeLabel ?? "",
            "Φορέας Έκδοσης": d?.organizationLabel ?? "",
            "Σύνδεσμος Απόφασης": d?.documentUrl ?? "",
          });
        });
      }
    });

    // Safety net: αν για κάποιο λόγο δεν προστέθηκε καμία γραμμή, βάλε τουλάχιστον τη βασική
    if (!pushedAtLeastOne) {
      rows.push(baseRow);
    }
  });

  return rows;
};


// Single AFM -> ένα ενιαίο sheet με όλες τις γραμμές,
// αλλά με διαφορετική "Ενότητα" (Α ή Β)
// και στήλες ευθυγραμμισμένες με το MultipleFundingByAfmPdfDocument
const buildSingleAfmExcelRows = (afmRecords, voucherRecords) => {
  const rows = [];

  // Ενότητα Α: σε επίπεδο ΑΦΜ
  if (afmRecords && afmRecords.length) {
    afmRecords.forEach((record) => {
      // Βασικές στήλες (όπως πριν) + θα προστεθούν τα Diavgeia πεδία
      const baseRow = {
        "Ενότητα": "Α",
        "ΑΦΜ": getFieldValue(record, ["AfmValue", "afm", "ΑΦΜ"]),
        "Χρήστης": getFieldValue(record, ["username", "Χρήστης"]),
        "Κωδικοί προγραμμάτων (CN codes)": getFieldValue(record, [
          "cn_codes",
          "cnCodes",
          "cn",
          "CN",
        ]),
        "Ημερομηνία & ώρα κλήσης": getFieldValue(record, [
          "datetime",
          "dateTime",
          "date",
          "called_at",
          "Ημερομηνία κλήσης",
        ]),
      };

      // Όμοια λογική με buildAfmFileExcelRows:
      let diavgeia = record.diavgeia_decisions;

      // Αν έρχεται ως JSON string → parse
      if (typeof diavgeia === "string") {
        try {
          diavgeia = JSON.parse(diavgeia);
        } catch (e) {
          console.error(
            "Failed to parse diavgeia_decisions in single AFM rows",
            e
          );
          diavgeia = null;
        }
      }

      // Αν δεν υπάρχει/είναι κενό → μόνο η βασική γραμμή
      if (!diavgeia || !Array.isArray(diavgeia) || diavgeia.length === 0) {
        rows.push(baseRow);
        return;
      }

      let pushedAtLeastOne = false;

      diavgeia.forEach((termEntry) => {
        const term = termEntry?.term ?? "";
        const decisions = Array.isArray(termEntry?.decisions)
          ? termEntry.decisions
          : [];

        if (decisions.length === 0) {
          // Γραμμή μόνο με term, χωρίς επιμέρους αποφάσεις
          rows.push({
            ...baseRow,
            "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
            "ΑΔΑ": "",
            "Αριθμός Πρωτοκόλλου": "",
            "Ημερομηνία έκδοσης": "",
            "Ημερομηνία ανάρτησης": "",
            "Θέμα": "",
            "Τύπος Απόφασης": "",
            "Φορέας Έκδοσης": "",
            "Σύνδεσμος Απόφασης": "",
          });
        } else {
          // Μία γραμμή ανά απόφαση
          decisions.forEach((d) => {
            pushedAtLeastOne = true;
            rows.push({
              ...baseRow,
              "Κωδικός Σχετικής Πρότασης σε άλλο Πρόγραμμα": term,
              "ΑΔΑ": d?.ada ?? "",
              "Αριθμός Πρωτοκόλλου": d?.protocolNumber ?? "",
              "Ημερομηνία έκδοσης": d?.issueDate ?? "",
              "Ημερομηνία ανάρτησης": d?.submissionTimestamp ?? "",
              "Θέμα": d?.subject ?? "",
              "Τύπος Απόφασης": d?.decisionTypeLabel ?? "",
              "Φορέας Έκδοσης": d?.organizationLabel ?? "",
              "Σύνδεσμος Απόφασης": d?.documentUrl ?? "",
            });
          });
        }
      });

      // Safety net: αν για κάποιο λόγο δεν μπήκε καμία γραμμή με αποφάσεις
      if (!pushedAtLeastOne) {
        rows.push(baseRow);
      }
    });
  }

  // Ενότητα Β: αφήνεις τον κώδικα σου όπως ήταν
  if (voucherRecords && voucherRecords.length) {
    const metaKeys = new Set([
      "username",
      "Χρήστης",
      "cn_codes",
      "cnCodes",
      "cn",
      "CN",
      "ada",
      "ΑΔΑ",
      "datetime",
      "dateTime",
      "date",
      "called_at",
      "Ημερομηνία κλήσης",
    ]);

    const allKeys = new Set();
    voucherRecords.forEach((r) => {
      Object.keys(r || {}).forEach((k) => allKeys.add(k));
    });

    const baseKeys = Array.from(allKeys).filter((k) => !metaKeys.has(k));

    voucherRecords.forEach((record) => {
      const row = {
        "Ενότητα": "Β",
      };

      baseKeys.forEach((k) => {
        row[k] =
          record && record[k] !== undefined && record[k] !== null
            ? String(record[k])
            : "";
      });

      row["Χρήστης"] = getFieldValue(record, ["username", "Χρήστης"]);
      row["Κωδικοί προγραμμάτων (CN codes)"] = getFieldValue(record, [
        "cn_codes",
        "cnCodes",
        "cn",
        "CN",
      ]);
      row["ΑΔΑ"] = getFieldValue(record, ["ada", "ΑΔΑ"]);
      row["Ημερομηνία & ώρα κλήσης"] = getFieldValue(record, [
        "datetime",
        "dateTime",
        "date",
        "called_at",
        "Ημερομηνία κλήσης",
      ]);

      rows.push(row);
    });
  }

  return rows;
};



// ======================
// PDF Template A: Αρχείο ΑΦΜ
// ======================
const AfmFilePdfDocument = ({ records, username, searchName }) => {
  // Header: ΠΑΝΤΑ τρέχουσα ημερομηνία/ώρα
  const headerDate = formatDateTimeForHeader(new Date());

  return (
    <Document
      title={`Διπλή χρηματοδότηση - ΑΦΜ (${searchName || ""})`}
      author={username}
      creator={username}
      producer={username}
    >
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
      >
        <Header
          username={username}
          date={headerDate}
          formname="Έλεγχος διπλής χρηματοδότησης ΑΦΜ"
        />

        <Text1 style={pdfStyles.infoText} wrap>
          Αρχείο αναζήτησης ΑΦΜ – {searchName || ""}
        </Text1>

        <View style={pdfStyles.container}>
          {records && records.length > 0 ? (
            <View style={pdfStyles.tableStyle}>
              {/* Headers */}
              <View style={pdfStyles.tableHeaderRow}>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Αριθμός Φορολογικού Μητρώου (ΑΦΜ)
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Κωδικοί πράξεων
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Αριθμοί Διαδικτυακής Ανάρτησης (ΑΔΑ)
                  </Text1>
                </View>
              </View>

              {/* Rows */}
              {records.map((record, i) => {
                const afmVal = getFieldValue(record, ["AfmValue", "afm", "ΑΦΜ"]);
                const cnVal = getFieldValue(record, [
                  "cn_codes",
                  "cnCodes",
                  "cn",
                  "CN",
                ]);
                const adaVal = getFieldValue(record, ["ada", "ΑΔΑ"]);

                return (
                  <View style={pdfStyles.tableRow} key={`${afmVal}_${i}`}>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(afmVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(cnVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(adaVal)}
                      </Text1>
                    </View>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text1 style={pdfStyles.infoText} wrap>
              Δεν βρέθηκαν εγγραφές για την παρούσα αναζήτηση ΑΦΜ.
            </Text1>
          )}
        </View>

        <Text1
          style={pdfStyles.footer}
          render={({ pageNumber, totalPages }) =>
            `ΕΥΔ ΣΣ ΚΑΠ, Μονάδα Χρηματοοικονομικών Ροών και Πληροφοριακών Συστημάτων            ${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

// ======================
// PDF Template B: Αρχείο Vouchers
// ======================
const VoucherFilePdfDocument = ({ records, username, searchName }) => {
  // Header: ΠΑΝΤΑ τρέχουσα ημερομηνία/ώρα
  const headerDate = formatDateTimeForHeader(new Date());

  return (
    <Document
      title={`Διπλή χρηματοδότηση - Vouchers (${searchName || ""})`}
      author={username}
      creator={username}
      producer={username}
    >
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
      >
        <Header
          username={username}
          date={headerDate}
          formname="Έλεγχος διπλής χρηματοδότησης τιμολογίου"
        />

        <Text1 style={pdfStyles.infoText} wrap>
          Αναζήτηση τιμολογίων – {searchName || ""}
        </Text1>

        <View style={pdfStyles.container}>
          {records && records.length > 0 ? (
            <View style={pdfStyles.tableStyle}>
              {/* Σταθερά headers */}
              <View style={pdfStyles.tableHeaderRow}>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    ΑΦΜ
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Αριθμός Τιμολογίου
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Σειρά Τιμολογίου
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Ημερομηνία Τιμολογίου
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    ΑΦΜ Εκδότη
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Κωδικοί πράξεων
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Αριθμός Διαδικτυακής Ανάρτησης (ΑΔΑ)
                  </Text1>
                </View>
              </View>

              {/* Σταθερές στήλες ανά εγγραφή */}
              {records.map((record, idx) => {
                const afmVal = getFieldValue(record, ["afm", "ΑΦΜ", "AfmValue"]);
                const voucherNumberVal = getFieldValue(record, [
                  "voucher",
                  "voucher_number",
                  "invoice_number",
                  "InvoiceNumber",
                  "Αριθμός Τιμολογίου",
                  "Αριθμός Τιμολόγιου",
                ]);
                const voucherSeriesVal = getFieldValue(record, [
                  "voucher_series",
                  "Σειρά",
                  "Σειρά Τιμολογίου",
                ]);
                const voucherDateVal = getFieldValue(record, [
                  "voucher_date",
                  "Ημερομηνία Τιμολογίου",
                  "Ημερομηνία",
                ]);
                const voucherVatVal = getFieldValue(record, [
                  "voucher_vat",
                  "ΦΠΑ",
                  "ΑΦΜ Εκδότη",
                ]);
                const cnVal = getFieldValue(record, [
                  "cn_codes",
                  "cnCodes",
                  "cn",
                  "CN",
                ]);
                const adaVal = getFieldValue(record, ["ada", "ΑΔΑ"]);
                // Ημερομηνία & ώρα κλήσης: ΜΟΝΟ από backend, χωρίς fallback σε now

                return (
                  <View style={pdfStyles.tableRow} key={idx}>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(afmVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(voucherNumberVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(voucherSeriesVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(voucherDateVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(voucherVatVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(cnVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(adaVal)}
                      </Text1>
                    </View>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text1 style={pdfStyles.infoText} wrap>
              Δεν βρέθηκαν εγγραφές τιμολογίων για την παρούσα αναζήτηση.
            </Text1>
          )}
        </View>

        <Text1
          style={pdfStyles.footer}
          render={({ pageNumber, totalPages }) =>
            `ΕΥΔ ΣΣ ΚΑΠ, Μονάδα Χρηματοοικονομικών Ροών και Πληροφοριακών Συστημάτων            ${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

// ======================
// PDF Template C: Μοναδικό ΑΦΜ
// ======================
const MultipleFundingByAfmPdfDocument = ({
  afmRecords,
  voucherRecords,
  username,
  afm,
}) => {
  // Header: ΠΑΝΤΑ τρέχουσα ημερομηνία/ώρα
  const headerDate = formatDateTimeForHeader(new Date());

  const hasAfm = afmRecords && afmRecords.length > 0;
  const hasVouchers = voucherRecords && voucherRecords.length > 0;

  return (
    <Document
      title={`Διπλή χρηματοδότηση - ${afm}`}
      author={username}
      creator={username}
      producer={username}
    >
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
      >
        <Header
          username={username}
          date={headerDate}
          formname="Ιστορικό διπλής χρηματοδότησης ΑΦΜ"
        />

        <Text1 style={pdfStyles.infoText} wrap>
          Αναλυτική αναφορά διπλής χρηματοδότησης για ΑΦΜ {afm}
        </Text1>

        {/* Ενότητα Α - ΑΦΜ */}
        <Text1 style={pdfStyles.sectionTitle} wrap>
          Ενότητα Α: Εγγραφές σε επίπεδο ΑΦΜ
        </Text1>
        <View style={pdfStyles.container}>
          {hasAfm ? (
            <View style={pdfStyles.tableStyle}>
              <View style={pdfStyles.tableHeaderRow}>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    ΑΦΜ
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Χρήστης
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Κωδικοί προγραμμάτων
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Αριθμοί Διαδικτυακής Ανάρτησης (ΑΔΑ)
                  </Text1>
                </View>
                <View style={pdfStyles.tableHeaderCol}>
                  <Text1 style={pdfStyles.tableHeader} wrap>
                    Ημερομηνία & ώρα κλήσης
                  </Text1>
                </View>
              </View>

              {afmRecords.map((record, i) => {
                const afmVal = getFieldValue(record, ["AfmValue", "afm", "ΑΦΜ", "afm_param"]);
                const userVal = getFieldValue(record, ["username", "Χρήστης"]);
                const cnVal = getFieldValue(record, [
                  "cn_codes",
                  "cnCodes",
                  "cn",
                  "CN",
                ]);
                const adaVal = getFieldValue(record, ["ada", "ΑΔΑ"]);
                // Ημερομηνία & ώρα κλήσης: ΜΟΝΟ από backend, χωρίς fallback
                const dtVal = getFieldValue(record, [
                  "datetime",
                  "dateTime",
                  "date",
                  "called_at",
                  "Ημερομηνία κλήσης",
                ]);

                return (
                  <View style={pdfStyles.tableRow} key={`${afmVal}_${i}`}>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(afmVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(userVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(cnVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(adaVal)}
                      </Text1>
                    </View>
                    <View style={pdfStyles.tableCol}>
                      <Text1 style={pdfStyles.tableCell} wrap>
                        {splitByCommaToLines(dtVal)}
                      </Text1>
                    </View>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text1 style={pdfStyles.infoText} wrap>
              Δεν βρέθηκαν εγγραφές διπλής χρηματοδότησης σε επίπεδο ΑΦΜ.
            </Text1>
          )}
        </View>

        {/* Ενότητα Β - Vouchers */}
        <Text1 style={pdfStyles.sectionTitle} wrap>
          Ενότητα Β: Εγγραφές σε επίπεδο τιμολογίου (vouchers)
        </Text1>
        <View style={pdfStyles.container}>
          {hasVouchers ? (
            (() => {
              let baseHeaders = [];
              const records = voucherRecords;
              const metaKeys = new Set([
                "username",
                "Χρήστης",
                "cn_codes",
                "cnCodes",
                "cn",
                "CN",
                "ada",
                "ΑΔΑ",
                "datetime",
                "dateTime",
                "date",
                "called_at",
                "Ημερομηνία κλήσης",
              ]);

              if (records && records.length > 0) {
                const allKeys = new Set();
                records.forEach((r) => {
                  Object.keys(r).forEach((k) => allKeys.add(k));
                });
                baseHeaders = Array.from(allKeys).filter(
                  (k) => !metaKeys.has(k)
                );
              }

              const headers = [
                ...baseHeaders,
                "username",
                "cn_codes",
                "ada",
                "Ημερομηνία & ώρα κλήσης",
              ];

              const headerLabel = (h) => {
                if (h === "username") return "Χρήστης";
                if (h === "cn_codes")
                  return "Κωδικοί προγραμμάτων (CN codes)";
                if (h === "ada") return "ΑΔΑ";
                if (h === "Ημερομηνία & ώρα κλήσης") return h;
                return h;
              };

              return (
                <View style={pdfStyles.tableStyle}>
                  <View style={pdfStyles.tableHeaderRow}>
                    {headers.map((h) => (
                      <View style={pdfStyles.tableHeaderCol} key={h}>
                        <Text1 style={pdfStyles.tableHeader} wrap>
                          {headerLabel(h)}
                        </Text1>
                      </View>
                    ))}
                  </View>

                  {records.map((record, idx) => {
                    // Ημερομηνία & ώρα κλήσης: ΜΟΝΟ από backend
                    const dtVal = getFieldValue(record, [
                      "datetime",
                      "dateTime",
                      "date",
                      "called_at",
                      "Ημερομηνία κλήσης",
                    ]);

                    return (
                      <View style={pdfStyles.tableRow} key={idx}>
                        {headers.map((h) => {
                          let val = "";
                          if (h === "username") {
                            val = getFieldValue(record, [
                              "username",
                              "Χρήστης",
                            ]);
                          } else if (h === "cn_codes") {
                            val = getFieldValue(record, [
                              "cn_codes",
                              "cnCodes",
                              "cn",
                              "CN",
                            ]);
                          } else if (h === "ada") {
                            val = getFieldValue(record, ["ada", "ΑΔΑ"]);
                          } else if (h === "Ημερομηνία & ώρα κλήσης") {
                            val = dtVal;
                          } else {
                            val =
                              record[h] !== undefined && record[h] !== null
                                ? String(record[h])
                                : "";
                          }

                          const displayVal = splitByCommaToLines(val);

                          return (
                            <View style={pdfStyles.tableCol} key={h}>
                              <Text1 style={pdfStyles.tableCell} wrap>
                                {displayVal}
                              </Text1>
                            </View>
                          );
                        })}
                      </View>
                    );
                  })}
                </View>
              );
            })()
          ) : (
            <Text1 style={pdfStyles.infoText} wrap>
              Δεν βρέθηκαν εγγραφές διπλής χρηματοδότησης σε επίπεδο
              τιμολογίου.
            </Text1>
          )}
        </View>

        <Text1
          style={pdfStyles.footer}
          render={({ pageNumber, totalPages }) =>
            `ΕΥΔ ΣΣ ΚΑΠ, Μονάδα Χρηματοοικονομικών Ροών και Πληροφοριακών Συστημάτων            ${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

function SearchModules() {
  const toast = useToast();
  const [cookies] = useCookies(["creds"]);

  const { username: cookieUsername, userid: cookieUserid } =
    getUserMetaFromCookies(cookies);

  // Θεωρούμε logged-in αν υπάρχει cookie "creds" με μη κενή τιμή στο document.cookie
  const isUserLoggedIn = React.useMemo(() => {
    if (typeof document === "undefined") return false;

    const hasCredsCookie = document.cookie
      .split(";")
      .some((c) => {
        const trimmed = c.trim();
        if (!trimmed.startsWith("creds=")) return false;
        const value = trimmed.split("=").slice(1).join("=").trim();
        return value !== ""; // να μην είναι creds= σκέτο
      });

    return hasCredsCookie;
  }, [cookies]);

  const ensureLoggedInOrToast = () => {
    if (!isUserLoggedIn) {
      toast({
        title: "Απαιτείται σύνδεση",
        description:
          "Για να εκτελέσετε αυτή την ενέργεια, πρέπει πρώτα να συνδεθείτε μέσω του μενού Σύνδεσης.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const textColor = useColorModeValue("gray.700", "white");
  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  // TAB STATE
  const [activeTab, setActiveTab] = useState("afmFile"); // 'afmFile' | 'voucherFile' | 'afmSingle'

  // dd/MM/yyyy HH:mm:ss για προβολή σε πίνακες ιστορικού
// dd/MM/yyyy HH:mm:ss για προβολή σε πίνακες ιστορικού
const formatDateTime = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();

    // Αν είναι ήδη dd/MM/yyyy HH:mm:ss, το κρατάμε όπως είναι
    if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }

  // Fallback: ISO, Date object, timestamp κ.λπ.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    // μην επιστρέφεις NaN/NaN/NaN...
    return "";
  }

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};


  const parseExcelFile = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheet = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheet];
    const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
    return json;
  };

  const validateExcelColumns = (rows, requiredColumns) => {
    if (!rows || rows.length === 0) {
      return {
        valid: false,
        error: "Το αρχείο δεν περιέχει γραμμές δεδομένων.",
      };
    }
    const keys = Object.keys(rows[0] || {});
    const missing = requiredColumns.filter((col) => !keys.includes(col));
    if (missing.length > 0) {
      return {
        valid: false,
        error: `Λείπουν υποχρεωτικές στήλες: ${missing.join(", ")}`,
      };
    }
    return { valid: true };
  };

  // Λογική για εξαγωγή σε XLSX από JSON (χρησιμοποιεί τα keys όπως τα περνάμε)
  const ExportXlsx = (rows, fileName, sheetName = "data") => {
    if (!rows || !rows.length) return;

    // Αν κάποια τιμή είναι array, κάνε την απλό string για ασφάλεια
    const newData = rows.map((item) => {
      const newItem = {};
      Object.keys(item).forEach((key) => {
        const val = item[key];
        if (Array.isArray(val)) {
          newItem[key] = val.join(", ");
        } else if (val === null || val === undefined) {
          newItem[key] = "";
        } else {
          newItem[key] = val;
        }
      });
      return newItem;
    });

    const ws = XLSX.utils.json_to_sheet(newData);
    const wb = { Sheets: { [sheetName]: ws }, SheetNames: [sheetName] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, fileName + ".xlsx");
  };


  const generatePdfDocument = async (fileName, pdfDocumentComponent) => {
    try {
      const blob = await pdf(pdfDocumentComponent).toBlob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  // =============================
  //  ΕΝΟΤΗΤΑ (α): Αναζήτηση με αρχείο ΑΦΜ
  // =============================

  const fileInputARef = useRef(null);
  const [excelFileA, setExcelFileA] = useState(null);
  const [excelDataA, setExcelDataA] = useState(null);
  const [isLoadingA, setIsLoadingA] = useState(false);
  const [historyA, setHistoryA] = useState([]);
  // Filters - History A
  const [filterADate, setFilterADate] = useState("");
  const [filterADesc, setFilterADesc] = useState("");
  // Filters - History B
  const [filterBDate, setFilterBDate] = useState("");
  const [filterBDesc, setFilterBDesc] = useState("");

  const [searchNameA, setSearchNameA] = useState("");
  const filteredHistoryA = React.useMemo(() => {
    const qDate = filterADate.trim().toLowerCase();
    const qDesc = filterADesc.trim().toLowerCase();

    return (historyA || []).filter((row) => {
      const dateStr = (formatDateTime(row.date) || "").toLowerCase();
      const descStr = (row.package_name || row.description || "").toLowerCase();

      return dateStr.includes(qDate) && descStr.includes(qDesc);
    });
  }, [historyA, filterADate, filterADesc]);

  


  const baseSearchUrlA =
    process.env.REACT_APP_PRODUCTION_URL+"/createMultipleFundingAFM";
    //"http://167.172.108.253:5004/createMultipleFundingAFM";
  const baseHistoryUrlA =
    process.env.REACT_APP_PRODUCTION_URL+"/getHistoryMultipleFundingAFM";
  const downloadHistoryUrlA =
    process.env.REACT_APP_PRODUCTION_URL+"/getMultipleFundingAFM"; // group_id ή date
    //"http://167.172.108.253:5004/getMultipleFundingAFM"; // group_id ή date

  const requiredColumnsA = ["ΑΦΜ"];

  const onFileChangeA = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const rows = await parseExcelFile(file);
      const { valid, error } = validateExcelColumns(rows, requiredColumnsA);
      if (!valid) {
        toast({
          title: "Σφάλμα αρχείου",
          description: error,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        setExcelFileA(null);
        setExcelDataA(null);
        return;
      }
      setExcelFileA(file);
      setExcelDataA(rows);
      toast({
        title: "Επιτυχής φόρτωση",
        description: "Το αρχείο πέρασε τους ελέγχους ορθότητας.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Σφάλμα κατά την ανάγνωση του Excel",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
      setExcelFileA(null);
      setExcelDataA(null);
    }
  };

  const fetchHistoryA = useCallback(async () => {
    if (!ensureLoggedInOrToast()) return;

    try {
      const params = new URLSearchParams();
      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }

      const url = `${baseHistoryUrlA}?${params.toString()}`;

      const response = await fetch(url);
      if (!response.ok) return;
      const data = await response.json();
      if (data.myerror) {
        toast({
          title: "Σφάλμα ιστορικού",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }
      setHistoryA(data.history || []);
    } catch (error) {
      console.error(error);
    }
  }, [baseHistoryUrlA, cookieUsername, cookieUserid, toast, ensureLoggedInOrToast]);

  // ΣΚΟΠΙΜΑ ΔΕΝ έχουμε useEffect για αυτόματο γέμισμα ιστορικού.

  const handleSubmitA = async () => {
    if (!ensureLoggedInOrToast()) return;

    if (!excelDataA || excelDataA.length === 0) {
      toast({
        title: "Δεν έχουν φορτωθεί δεδομένα",
        description: "Παρακαλώ επισυνάψτε ένα έγκυρο αρχείο Excel.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const trimmedName = searchNameA.trim();
    if (!trimmedName || trimmedName.length > 50) {
      toast({
        title: "Μη έγκυρο όνομα αναζήτησης",
        description:
          "Παρακαλώ εισάγετε όνομα αναζήτησης (υποχρεωτικό, έως 50 χαρακτήρες).",
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsLoadingA(true);
    try {
      const matrixA = to2DArrayFromRows(excelDataA);

      const params = new URLSearchParams();
      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }
      params.append("searchName", trimmedName);
      params.append("data", JSON.stringify(matrixA));

      const urlWithData = `${baseSearchUrlA}?${params.toString()}`;

      const response = await fetch(urlWithData);

      if (!response.ok) {
        throw new Error("Σφάλμα απόκρισης server");
      }

      let text = await response.text();
      text = text.replace(/: *NaN/g, ': null');
      const data = JSON.parse(text);

      if (data.myerror) {
        toast({
          title: "Σφάλμα αναζήτησης",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      if (!hasAfmOrVoucherData(data)) {
        toast({
          title: "Δεν βρέθηκαν εγγραφές",
          description: "Δεν βρέθηκαν εγγραφές για το αποσταλμένο αρχείο.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      const records = extractRecordsFromData(data);

      await generatePdfDocument(
        `result_other_program_${Date.now()}.pdf`,
        <AfmFilePdfDocument
          records={records}
          username={cookieUsername}
          searchName={trimmedName}
        />
      );

      toast({
        title: "Η αναζήτηση ολοκληρώθηκε",
        description: "Τα αποτελέσματα εξήχθησαν σε αρχείο PDF.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Σφάλμα αναζήτησης",
        description: error.message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoadingA(false);
    }
  };

  // Popup για λήψη από ιστορικό (A/B)
  const {
    isOpen: isHistoryFileTypeOpen,
    onOpen: onHistoryFileTypeOpen,
    onClose: onHistoryFileTypeClose,
  } = useDisclosure();
  const historyCancelRef = useRef();
  const [historyFileType, setHistoryFileType] = useState("pdf"); // 'pdf' | 'xlsx'
  const [historyContext, setHistoryContext] = useState(null); // 'A' | 'B'
  const [historyPendingRow, setHistoryPendingRow] = useState(null);

  const openHistoryDialog = (context, row) => {
    setHistoryContext(context);
    setHistoryPendingRow(row);
    setHistoryFileType("pdf");
    onHistoryFileTypeOpen();
  };

  const handleConfirmHistoryDownload = async () => {
    onHistoryFileTypeClose();

    if (!historyPendingRow || !historyContext) {
      return;
    }

    if (!ensureLoggedInOrToast()) return;

    try {
      const baseUrl =
        historyContext === "A" ? downloadHistoryUrlA : downloadHistoryUrlB;

      const params = new URLSearchParams();
      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }

      // Backend ζητά group_id ή date
      if (historyPendingRow.group_id) {
        params.set("group_id", historyPendingRow.group_id);
      } else if (historyPendingRow.date) {
        params.set("date", historyPendingRow.date);
      } else if (historyPendingRow.id) {
        // fallback
        params.set("id", historyPendingRow.id);
      }

      const url = `${baseUrl}?${params.toString()}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error("Αποτυχία λήψης δεδομένων");

      const data = await response.json();

      if (data.myerror) {
        toast({
          title: "Σφάλμα λήψης",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      if (!hasAfmOrVoucherData(data)) {
        toast({
          title: "Δεν βρέθηκαν εγγραφές",
          description: "Δεν βρέθηκαν εγγραφές για τη συγκεκριμένη κλήση.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      if (historyContext === "A") {
        let afmRecords = [];
        if (Array.isArray(data)) {
          afmRecords = data;
        } else if (Array.isArray(data.afm)) {
          afmRecords = data.afm;
        } else {
          afmRecords = extractRecordsFromData(data);
        }

        if (historyFileType === "xlsx") {
          const excelRows = buildAfmFileExcelRows(afmRecords);
          ExportXlsx(
            excelRows,
            `multiple_funding_afm_history_${
              historyPendingRow.group_id || historyPendingRow.id || Date.now()
            }`
          );
        } else {
          await generatePdfDocument(
            `multiple_funding_afm_history_${
              historyPendingRow.group_id || historyPendingRow.id || Date.now()
            }.pdf`,
            <AfmFilePdfDocument
              records={afmRecords}
              username={cookieUsername}
              searchName={
                historyPendingRow.package_name ||
                historyPendingRow.description ||
                ""
              }
            />
          );
        }
      } else if (historyContext === "B") {
        let voucherRecordsRaw = [];
        if (Array.isArray(data)) {
          voucherRecordsRaw = data;
        } else if (Array.isArray(data.voucher)) {
          voucherRecordsRaw = data.voucher;
        } else {
          voucherRecordsRaw = extractRecordsFromData(data);
        }

        const voucherRecords = voucherRecordsRaw.map(mapVoucherHistoryRecord);

        if (historyFileType === "xlsx") {
          const excelRows = buildVoucherHistoryExcelRows(voucherRecords);
          ExportXlsx(
            excelRows,
            `multiple_funding_afm_history_${
              historyPendingRow.group_id || historyPendingRow.id || Date.now()
            }`
          );
        } else {
          await generatePdfDocument(
            `multiple_funding_voucher_history_${
              historyPendingRow.group_id || historyPendingRow.id || Date.now()
            }.pdf`,
            <VoucherFilePdfDocument
              records={voucherRecords}
              username={cookieUsername}
              searchName={
                historyPendingRow.package_name ||
                historyPendingRow.description ||
                ""
              }
            />
          );
        }
      }

      toast({
        title: "Η λήψη ολοκληρώθηκε",
        description: "Τα αποτελέσματα εξήχθησαν σε αρχείο.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Σφάλμα λήψης",
        description: error.message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const historyColumnsA = [
    {
      name: "Ημερομηνία",
      selector: (row) => formatDateTime(row.date),
      sortable: true,
    },
    {
      name: "Περιγραφή",
      selector: (row) => row.package_name || row.description || "",
      sortable: true,
    },
    {
      name: "Ενέργεια",
      cell: (row) => (
        <button
          style={{
            borderColor: bgButton,
            borderWidth: 1,
            borderRadius: 5,
            padding: 5,
          }}
          onClick={() => openHistoryDialog("A", row)}
        >
          Λήψη
        </button>
      ),
      allowOverflow: true,
      button: true,
    },
  ];

  // =============================
  //  ΕΝΟΤΗΤΑ (β): Αναζήτηση βάσει στοιχείων τιμολογίων
  // =============================

  const fileInputBRef = useRef(null);
  const [excelFileB, setExcelFileB] = useState(null);
  const [excelDataB, setExcelDataB] = useState(null);
  const [isLoadingB, setIsLoadingB] = useState(false);
  const [historyB, setHistoryB] = useState([]);
  const [searchNameB, setSearchNameB] = useState("");

  const filteredHistoryB = React.useMemo(() => {
    const qDate = filterBDate.trim().toLowerCase();
    const qDesc = filterBDesc.trim().toLowerCase();

    return (historyB || []).filter((row) => {
      const dateStr = (formatDateTime(row.date) || "").toLowerCase();
      const descStr = (row.package_name || row.description || "").toLowerCase();

      return dateStr.includes(qDate) && descStr.includes(qDesc);
    });
  }, [historyB, filterBDate, filterBDesc]);

  const baseSearchUrlB =
    process.env.REACT_APP_PRODUCTION_URL+"/createMultipleFundingVoucher";
  const baseHistoryUrlB =
    process.env.REACT_APP_PRODUCTION_URL+"/getHistoryMultipleFundingVoucher";
  const downloadHistoryUrlB =
    process.env.REACT_APP_PRODUCTION_URL+"/getMultipleFundingVoucher";
    //"http://167.172.108.253:5004/getMultipleFundingVoucher";

  const requiredColumnsB = [
    "ΑΦΜ",
    "Αριθμός Τιμολογίου",
    "Σειρά Τιμολογίου",
    "Ημερομηνία Τιμολογίου",
    "ΑΦΜ εκδότη",
  ];

  const onFileChangeB = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const rows = await parseExcelFile(file);
      const { valid, error } = validateExcelColumns(rows, requiredColumnsB);
      if (!valid) {
        toast({
          title: "Σφάλμα αρχείου",
          description: error,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        setExcelFileB(null);
        setExcelDataB(null);
        return;
      }

      // Normalise ημερομηνίες τιμολογίου σε dd/mm/yy
      const normalizedRows = rows.map((row) => {
        const rawDate = row["Ημερομηνία Τιμολογίου"];
        const normDate = normalizeVoucherDate(rawDate);
        return { ...row, "Ημερομηνία Τιμολογίου": normDate };
      });

      const invalidDates = normalizedRows
        .map((r) => r["Ημερομηνία Τιμολογίου"])
        .filter((val) => !isValidDDMMYY(val));

      if (invalidDates.length > 0) {
        toast({
          title: "Σφάλμα ημερομηνιών",
          description:
            "Υπάρχουν γραμμές με μη έγκυρη 'Ημερομηνία Τιμολογίου'. Η μορφή πρέπει να είναι dd/mm/yy.",
          status: "error",
          duration: 8000,
          isClosable: true,
        });
        setExcelFileB(null);
        setExcelDataB(null);
        return;
      }

      setExcelFileB(file);
      setExcelDataB(normalizedRows);
      toast({
        title: "Επιτυχής φόρτωση",
        description: "Το αρχείο τιμολογίων πέρασε τους ελέγχους.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Σφάλμα κατά την ανάγνωση του Excel",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
      setExcelFileB(null);
      setExcelDataB(null);
    }
  };

  const fetchHistoryB = useCallback(async () => {
    if (!ensureLoggedInOrToast()) return;

    try {
      const params = new URLSearchParams();

      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }

      const url = `${baseHistoryUrlB}?${params.toString()}`;

      const response = await fetch(url);
      if (!response.ok) {
        toast({
          title: "Σφάλμα ιστορικού",
          description: `Αποτυχία ανάκτησης ιστορικού (HTTP ${response.status})`,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      const data = await response.json();

      if (data?.myerror) {
        toast({
          title: "Σφάλμα ιστορικού",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      // ✅ Support BOTH response shapes:
      // 1) backend returns { history: [...] }
      // 2) backend returns [...] (array directly)
      const history = Array.isArray(data) ? data : data?.history || [];

      setHistoryB(history);
    } catch (error) {
      console.error("fetchHistoryB error:", error);
      toast({
        title: "Σφάλμα ιστορικού",
        description: "Παρουσιάστηκε σφάλμα κατά την ανάκτηση ιστορικού.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  }, [
    baseHistoryUrlB,
    cookies,
    cookieUsername,
    cookieUserid,
    toast,
    ensureLoggedInOrToast,
  ]);


  const handleSubmitB = async () => {
    if (!ensureLoggedInOrToast()) return;

    if (!excelDataB || excelDataB.length === 0) {
      toast({
        title: "Δεν έχουν φορτωθεί δεδομένα",
        description: "Παρακαλώ επισυνάψτε ένα έγκυρο αρχείο Excel τιμολογίων.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const trimmedName = searchNameB.trim();
    if (!trimmedName || trimmedName.length > 50) {
      toast({
        title: "Μη έγκυρο όνομα αναζήτησης",
        description:
          "Παρακαλώ εισάγετε όνομα αναζήτησης (υποχρεωτικό, έως 50 χαρακτήρες).",
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsLoadingB(true);
    try {
      const matrixB = to2DArrayFromRows(excelDataB);

      const params = new URLSearchParams();
      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }
      params.append("searchName", trimmedName);
      params.append("data", JSON.stringify(matrixB));

      const urlWithData = `${baseSearchUrlB}?${params.toString()}`;

      const response = await fetch(urlWithData);

      if (!response.ok) {
        throw new Error("Σφάλμα απόκρισης server");
      }

      const data = await response.json();

      if (data.myerror) {
        toast({
          title: "Σφάλμα αναζήτησης",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      if (!hasAfmOrVoucherData(data)) {
        toast({
          title: "Δεν βρέθηκαν εγγραφές",
          description: "Δεν βρέθηκαν εγγραφές για το αποσταλμένο αρχείο.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      const records = extractRecordsFromData(data);

      await generatePdfDocument(
        `result_invoices_${Date.now()}.pdf`,
        <VoucherFilePdfDocument
          records={records}
          username={cookieUsername}
          searchName={trimmedName}
        />
      );

      toast({
        title: "Η αναζήτηση ολοκληρώθηκε",
        description: "Τα αποτελέσματα εξήχθησαν σε αρχείο PDF.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Σφάλμα αναζήτησης",
        description: error.message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoadingB(false);
    }
  };

  const historyColumnsB = [
    {
      name: "Ημερομηνία",
      selector: (row) => formatDateTime(row.date),
      sortable: true,
    },
    {
      name: "Περιγραφή",
      selector: (row) => row.package_name || row.description || "",
      sortable: true,
    },
    {
      name: "Ενέργεια",
      cell: (row) => (
        <button
          style={{
            borderColor: bgButton,
            borderWidth: 1,
            borderRadius: 5,
            padding: 5,
          }}
          onClick={() => openHistoryDialog("B", row)}
        >
          Λήψη
        </button>
      ),
      allowOverflow: true,
      button: true,
    },
  ];

  // =============================
  //  ΕΝΟΤΗΤΑ (γ): Αναζήτηση AFM (single)
  // =============================

  const [searchCode, setSearchCode] = useState("");
  const [isLoadingC, setIsLoadingC] = useState(false);

  const {
    isOpen: isFileTypeOpen,
    onOpen: onFileTypeOpen,
    onClose: onFileTypeClose,
  } = useDisclosure();
  const cancelRef = useRef();
  const [selectedFileType, setSelectedFileType] = useState("pdf");
  const [pendingCode, setPendingCode] = useState("");

  const handleOpenFileTypeDialog = () => {
    const trimmed = searchCode.trim();

    if (!trimmed || trimmed.length !== 9) {
      toast({
        title: "Μη έγκυρο ΑΦΜ",
        description: "Το ΑΦΜ πρέπει να είναι ακριβώς 9 χαρακτήρες.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!ensureLoggedInOrToast()) return;

    setPendingCode(trimmed);
    onFileTypeOpen();
  };

  const handleSubmitC = async () => {
    if (!pendingCode || pendingCode.length !== 9) {
      onFileTypeClose();
      return;
    }

    onFileTypeClose();

    if (!ensureLoggedInOrToast()) return;

    setIsLoadingC(true);

    try {
      const baseUrl =
        process.env.REACT_APP_PRODUCTION_URL+"/getMultipleFundingByAfm";
        //"http://167.172.108.253:5004/getMultipleFundingByAfm";

      const params = new URLSearchParams();
      if ("creds" in cookies) {
        params.append("username", cookieUsername);
        params.append("userid", cookieUserid);
      } else {
        params.append("username", "");
        params.append("userid", "");
      }
      params.append("afm", pendingCode);

      const url = `${baseUrl}?${params.toString()}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Σφάλμα απόκρισης server");
      }

      const data = await response.json();

      if (data.myerror) {
        toast({
          title: "Σφάλμα αναζήτησης",
          description: data.myerror,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      let afmRecords = Array.isArray(data.afm) ? data.afm : [];
      let voucherRecords = Array.isArray(data.voucher) ? data.voucher : [];

      // Πιο ανεκτική λογική αν δεν υπάρχουν ξεχωριστά afm/voucher arrays
      if (
        !afmRecords.length &&
        !voucherRecords.length &&
        hasAfmOrVoucherData(data)
      ) {
        const combined = extractRecordsFromData(data);
        afmRecords = combined.filter(
          (r) => !("voucher" in r || "voucher_number" in r)
        );
        voucherRecords = combined.filter(
          (r) => "voucher" in r || "voucher_number" in r
        );
      }

      if (!afmRecords.length && !voucherRecords.length) {
        toast({
          title: "Δεν βρέθηκαν εγγραφές",
          description: `Δεν βρέθηκαν εγγραφές για το ΑΦΜ ${pendingCode}.`,
          status: "info",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      if (selectedFileType === "xlsx") {
        const excelRows = buildSingleAfmExcelRows(afmRecords, voucherRecords);
        ExportXlsx(
          excelRows,
          `multiple_funding_${pendingCode}_${Date.now()}`
        );
      } else {
        await generatePdfDocument(
          `multiple_funding_${pendingCode}_${Date.now()}.pdf`,
          <MultipleFundingByAfmPdfDocument
            afmRecords={afmRecords}
            voucherRecords={voucherRecords}
            username={cookieUsername}
            afm={pendingCode}
          />
        );
      }

      toast({
        title: "Η αναζήτηση ολοκληρώθηκε",
        description: "Τα αποτελέσματα εξήχθησαν σε αρχείο.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Σφάλμα αναζήτησης",
        description: error.message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoadingC(false);
    }
  };

  // =============================
  //  RENDER
  // =============================

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* Links πάνω από τα tabs */}
      <Flex
        justify="space-between"
        my="1rem"
        align="center"
        flexDirection="row"
        minHeight="60px"
        w="100%"
      >
        <a
          href={Manual}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "white", margin: "20px 0", fontWeight: "bold" }}
        >
          Οδηγίες χρήσης
        </a>
        <a
          href={Policy}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "white", margin: "20px 0", fontWeight: "bold" }}
        >
          Πολιτική Απορρήτου
        </a>
      </Flex>

      {/* TAB BAR */}
      <Box mb="4">
        <Flex gap={2} wrap="wrap">
          <Button
            onClick={() => setActiveTab("afmFile")}
            bg={activeTab === "afmFile" ? bgButton : "transparent"}
            color={activeTab === "afmFile" ? "white" : textColor}
            variant={activeTab === "afmFile" ? "solid" : "outline"}
            borderColor={bgButton}
            _hover={{ bg: bgButton, color: "white" }}
          >
            Αναζήτηση ΑΦΜ
          </Button>
          <Button
            onClick={() => setActiveTab("voucherFile")}
            bg={activeTab === "voucherFile" ? bgButton : "transparent"}
            color={activeTab === "voucherFile" ? "white" : textColor}
            variant={activeTab === "voucherFile" ? "solid" : "outline"}
            borderColor={bgButton}
            _hover={{ bg: bgButton, color: "white" }}
          >
            Αναζήτηση Τιμολογίων
          </Button>
          <Button
            onClick={() => setActiveTab("afmSingle")}
            bg={activeTab === "afmSingle" ? bgButton : "transparent"}
            color={activeTab === "afmSingle" ? "white" : textColor}
            variant={activeTab === "afmSingle" ? "solid" : "outline"}
            borderColor={bgButton}
            _hover={{ bg: bgButton, color: "white" }}
          >
            Αναζήτηση ανά ΑΦΜ
          </Button>
        </Flex>
      </Box>

      <Grid
        templateColumns={{ sm: "1fr", lg: "1fr" }}
        templateRows="1fr"
        gap="24px"
      >
        {/* ΕΝΟΤΗΤΑ (α) - αρχείο ΑΦΜ */}
        {activeTab === "afmFile" && (
          <Card my={{ lg: "24px" }} me={{ lg: "24px" }} w="100%">
            <Box margin="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4">
                Αναζήτηση ΑΦΜ σε άλλο πρόγραμμα χρηματοδότησης
              </Text>

              <Text color={textColor} fontSize="sm" mb="4">
                Επισυνάψτε αρχείο Excel με Αριθμούς Φορολογικού Μητρώου,
                σύμφωνα με την τυποποιημένη δομή, και το αποτέλεσμα θα εξαχθεί σε PDF. Απαιτείται επίσης
                ένα σύντομο όνομα αναζήτησης.
              </Text>

              <input
                ref={fileInputARef}
                type="file"
                accept=".xlsx,.xls"
                onChange={onFileChangeA}
              />
              <div style={{ marginTop: "4px", marginBottom: "8px" }}>
                <a
                  href={TemplateOtherProgram}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "0.875rem",
                    textDecoration: "underline",
                  }}
                >
                  Κατεβάστε πρότυπο αρχείο Excel
                </a>
              </div>

              <Input
                value={searchNameA}
                onChange={(e) => setSearchNameA(e.target.value.slice(0, 50))}
                maxLength={50}
                placeholder="Όνομα αναζήτησης (υποχρεωτικό, έως 50 χαρακτήρες)"
                bg="whiteAlpha.50"
                _placeholder={{ color: "gray.400" }}
                mb="2"
              />
              <Text fontSize="xs" mt="1" color={textColor}>
                Το όνομα αναζήτησης αποστέλλεται και αποθηκεύεται μαζί με τα
                στοιχεία.
              </Text>

              <Flex
                justify="center"
                my="1rem"
                align="center"
                flexDirection="column"
                minHeight="60px"
                w="100%"
              >
                <Button
                  bg={bgButton}
                  color="white"
                  fontSize="md"
                  variant="no-hover"
                  onClick={handleSubmitA}
                  w="100%"
                  mt="4"
                  isDisabled={!excelDataA || isLoadingA || !searchNameA.trim()}
                >
                  Εκτέλεση αναζήτησης
                </Button>

                <Button
                  bg={bgButton}
                  my="1rem"
                  color="white"
                  fontSize="md"
                  variant="no-hover"
                  onClick={fetchHistoryA}
                  w="100%"
                >
                  Ιστορικό κλήσεων
                </Button>
              </Flex>

              {isLoadingA && <LoadingSpinner />}

              {!isLoadingA && historyA && historyA.length > 0 && (
                <DataTable
                  columns={historyColumnsA}
                  data={filteredHistoryA}
                  pagination
                  theme="dark"
                  persistTableHead
                  subHeader
                  subHeaderComponent={
                    <Flex gap={2} align="center" wrap="wrap" w="100%">
                      <Input
                        value={filterADate}
                        onChange={(e) => setFilterADate(e.target.value)}
                        placeholder="Φίλτρο Ημερομηνίας (π.χ. 10/01/2026)"
                        bg="whiteAlpha.100"
                      />
                      <Input
                        value={filterADesc}
                        onChange={(e) => setFilterADesc(e.target.value)}
                        placeholder="Φίλτρο Περιγραφής"
                        bg="whiteAlpha.100"
                      />
                      <Button
                        onClick={() => {
                          setFilterADate("");
                          setFilterADesc("");
                        }}
                        variant="outline"
                      >
                        Καθαρισμός
                      </Button>
                    </Flex>
                  }
                />
              )}

            </Box>
          </Card>
        )}

        {/* ΕΝΟΤΗΤΑ (β) - αρχείο τιμολογίων */}
        {activeTab === "voucherFile" && (
          <Card my={{ lg: "24px" }} me={{ lg: "24px" }} w="100%">
            <Box margin="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4">
                Αναζήτηση τιμολογίων σε άλλο πρόγραμμα χρηματοδότησης
              </Text>

              <Text color={textColor} fontSize="sm" mb="4">
                Επισυνάψτε αρχείο Excel με στοιχεία τιμολογίων, σύμφωνα με την
                τυποποιημένη δομή, και το αποτέλεσμα θα
                εξαχθεί σε PDF. Απαιτείται επίσης ένα σύντομο όνομα αναζήτησης.
              </Text>

              <input
                ref={fileInputBRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={onFileChangeB}
              />
              <div style={{ marginTop: "4px", marginBottom: "8px" }}>
                <a
                  href={TemplateInvoices}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "0.875rem",
                    textDecoration: "underline",
                  }}
                >
                  Κατεβάστε πρότυπο αρχείο Excel
                </a>
              </div>

              <Input
                value={searchNameB}
                onChange={(e) => setSearchNameB(e.target.value.slice(0, 50))}
                maxLength={50}
                placeholder="Όνομα αναζήτησης (υποχρεωτικό, έως 50 χαρακτήρες)"
                bg="whiteAlpha.50"
                _placeholder={{ color: "gray.400" }}
                mb="2"
              />
              <Text fontSize="xs" mt="1" color={textColor}>
                Το όνομα αναζήτησης αποστέλλεται και αποθηκεύεται μαζί με τα
                στοιχεία.
              </Text>

              <Flex
                justify="center"
                my="1rem"
                align="center"
                flexDirection="column"
                minHeight="60px"
                w="100%"
              >
                <Button
                  bg={bgButton}
                  color="white"
                  fontSize="md"
                  variant="no-hover"
                  onClick={handleSubmitB}
                  w="100%"
                  mt="4"
                  isDisabled={!excelDataB || isLoadingB || !searchNameB.trim()}
                >
                  Εκτέλεση αναζήτησης
                </Button>

                <Button
                  bg={bgButton}
                  my="1rem"
                  color="white"
                  fontSize="md"
                  variant="no-hover"
                  onClick={fetchHistoryB}
                  w="100%"
                >
                  Ιστορικό κλήσεων
                </Button>
              </Flex>

              {isLoadingB && <LoadingSpinner />}

              {!isLoadingB && historyB && historyB.length > 0 && (
                <DataTable
                  columns={historyColumnsB}
                  data={filteredHistoryB}
                  pagination
                  theme="dark"
                  persistTableHead
                  subHeader
                  subHeaderComponent={
                    <Flex gap={2} align="center" wrap="wrap" w="100%">
                      <Input
                        value={filterBDate}
                        onChange={(e) => setFilterBDate(e.target.value)}
                        placeholder="Φίλτρο Ημερομηνίας (π.χ. 10/01/2026)"
                        bg="whiteAlpha.100"
                      />
                      <Input
                        value={filterBDesc}
                        onChange={(e) => setFilterBDesc(e.target.value)}
                        placeholder="Φίλτρο Περιγραφής"
                        bg="whiteAlpha.100"
                      />
                      <Button
                        onClick={() => {
                          setFilterBDate("");
                          setFilterBDesc("");
                        }}
                        variant="outline"
                      >
                        Καθαρισμός
                      </Button>
                    </Flex>
                  }
                />
              )}
            </Box>
          </Card>
        )}

        {/* ΕΝΟΤΗΤΑ (γ) - μοναδικό ΑΦΜ */}
        {activeTab === "afmSingle" && (
          <Card my={{ lg: "24px" }} me={{ lg: "24px" }} w="100%">
            <Box margin="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4">
                Αναζήτηση διπλής χρηματοδότησης ανά ΑΦΜ
              </Text>

              <Text color={textColor} fontSize="sm" mb="4">
                Εισάγετε ένα ΑΦΜ (9 χαρακτήρες). Το σύστημα θα λάβει τα
                δεδομένα και δύναται να τα εξάγει σε PDF ή σε Excel.
              </Text>

              <Input
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                maxLength={9}
                placeholder="Εισάγετε ΑΦΜ (9 χαρακτήρες)"
                bg="whiteAlpha.50"
                _placeholder={{ color: "gray.400" }}
              />
              <Text fontSize="xs" mt="1" color={textColor}>
                Το ΑΦΜ πρέπει να είναι ακριβώς 9 χαρακτήρες.
              </Text>

              <Flex
                justify="center"
                my="1rem"
                align="center"
                flexDirection="column"
                minHeight="60px"
                w="100%"
              >
                <Button
                  bg={bgButton}
                  color="white"
                  fontSize="md"
                  variant="no-hover"
                  onClick={handleOpenFileTypeDialog}
                  w="100%"
                  mt="4"
                  isDisabled={!searchCode.trim() || isLoadingC}
                >
                  Αναζήτηση
                </Button>
              </Flex>

              {isLoadingC && <LoadingSpinner />}
            </Box>
          </Card>
        )}
      </Grid>

      {/* Popup επιλογής τύπου αρχείου για ΑΦΜ (ενότητα γ) */}
      <AlertDialog
        isOpen={isFileTypeOpen}
        leastDestructiveRef={cancelRef}
        onClose={onFileTypeClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Επιλογή τύπου αρχείου
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                Επιλέξτε τον τύπο αρχείου με τον οποίο θέλετε να εξαχθούν τα
                αποτελέσματα για το ΑΦΜ <strong>{pendingCode}</strong>.
              </Text>
              <RadioGroup
                onChange={setSelectedFileType}
                value={selectedFileType}
              >
                <Stack direction="column" spacing={2}>
                  <Radio value="pdf">PDF</Radio>
                  <Radio value="xlsx">Excel (XLSX)</Radio>
                </Stack>
              </RadioGroup>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onFileTypeClose}>
                Άκυρο
              </Button>
              <Button colorScheme="blue" onClick={handleSubmitC} ml={3}>
                Συνέχεια
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Popup επιλογής τύπου αρχείου για Ιστορικό (A/B) */}
      <AlertDialog
        isOpen={isHistoryFileTypeOpen}
        leastDestructiveRef={historyCancelRef}
        onClose={onHistoryFileTypeClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Επιλογή τύπου αρχείου για λήψη
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                Επιλέξτε τον τύπο αρχείου με τον οποίο θέλετε να εξαχθούν τα
                αποτελέσματα για την επιλεγμένη κλήση ιστορικού.
              </Text>
              <RadioGroup
                onChange={setHistoryFileType}
                value={historyFileType}
              >
                <Stack direction="column" spacing={2}>
                  <Radio value="pdf">PDF</Radio>
                  <Radio value="xlsx">Excel (XLSX)</Radio>
                </Stack>
              </RadioGroup>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={historyCancelRef}
                onClick={onHistoryFileTypeClose}
              >
                Άκυρο
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmHistoryDownload}
                ml={3}
              >
                Συνέχεια
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

export default SearchModules;
